import { trackDemoBookedConversion, trackFormSubmittedConversion } from "@/lib/ad-conversions";
import * as analytics from "@/lib/analytics";

/**
 * Marketing site funnel:
 *
 * 1. `$pageview` (fired by `PostHogPageView` in the marketing layout) covers
 *    discovery: landings, guides, blog posts, compare pages, all by pathname.
 * 2. "Marketing CTA Clicked": a conversion-intent link (signup, book a demo,
 *    contact, log in) was clicked, fired by `MarketingCtaTracking`'s
 *    delegated listener so every CTA on every page is covered.
 * 3. "Marketing Form Submitted": a demo, contact, or partner form completed.
 *    For `demo` this is the brand-type pick that opens the cal.com scheduler.
 * 4. "Marketing Demo Booked": the visitor booked a time in that scheduler.
 * 5. "Pricing Billing Interval Selected": monthly/annual toggle on /pricing.
 *
 * Marketing visitors are anonymous, so these events carry no `gym`; the
 * funnel joins to product adoption through PostHog identity merge once the
 * visitor signs up. Form submissions and bookings also report to GTM and the
 * Meta Pixel through `@/lib/ad-conversions` for ad attribution; PostHog is
 * the PLG source of truth.
 */

export function trackMarketingCtaClicked(props: {
  /** Visible label of the clicked link. */
  cta: string;
  /** Destination pathname (`/signup`, `/book-a-demo`, ...). */
  href: string;
  /** Pathname of the page the click happened on. */
  location: string;
}) {
  analytics.track("Marketing CTA Clicked", props);
}

export type MarketingForm = "demo" | "contact" | "bfs" | "mariana-tek";

export function trackMarketingFormSubmitted(form: MarketingForm, props?: { brandType?: string }) {
  analytics.track("Marketing Form Submitted", { form, ...props });
  trackFormSubmittedConversion(form, props);
}

export function trackMarketingDemoBooked(brandType: string) {
  analytics.track("Marketing Demo Booked", { brandType });
  trackDemoBookedConversion(brandType);
}

export function trackPricingIntervalSelected(interval: "month" | "year") {
  analytics.track("Pricing Billing Interval Selected", { interval });
}
